import { FunctionComponent } from 'preact';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { MoreVideosButton } from '../moreVideosButton/moreVideosButton';
import { GalleryPlaylistView } from '../galleryPlaylistView/galleryPlaylistView';
import { videoDataSelector } from '../../../common/store/selectors/videoData.selector';
import { MORE_VIDEOS_SHOW_GALLERY_PLAYLIST_VIEW_SECONDS_TO_END } from '../moreVideos.constants';
import { userInteractionSelector } from '../../../common/store/selectors/userInteraction.selector';
import { useStyles } from '../../../common/hooks/useStyles';
import { getMoreVideosStyleObject } from './moreVideos.style';
import { removeAdCustomParams, setAdCustomParams, setShowGalleryView } from '../store/moreVideos.actions';
import { moreVideosSelector } from '../../../common/store/selectors/moreVideos.selector';
import { mediaDataSelector } from '../../../common/store/selectors/mediaData.selector';
import { MiddleScreenPlaylistView } from '../middleScreenPlaylistView/middleScreenPlaylistView';
import { adsDataSelector } from '../../../common/store/selectors/adsData.selector';
import { systemGeneralSelector } from '../../../common/store/selectors/systemGeneral.selector';
import { ErrorBoundary } from '../../../common/components/errorBoundary/errorBoundary';

interface MoreVideosProps {
  style: any;
}

const MORE_VIDEOS_PLUGIN = 'MoreVideos';

const MoreVideos: FunctionComponent<MoreVideosProps> = props => {
  const { style } = props;
  const dispatch = useDispatch();
  const playerSize = useSelector(playerDataSelector.playerSize);
  const showControls = useSelector(playerDataSelector.showControls);
  const playbackMethod = useSelector(playerDataSelector.playbackMethod);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const videoDuration = useSelector(videoDataSelector.duration);
  const currentVideoTimeFragment = useSelector(playerDataSelector.currentVideoTimeFragment);
  const userInteractionType = useSelector(userInteractionSelector.userInteractionType);
  const showGalleryView = useSelector(moreVideosSelector.showGalleryView);
  const videoList = useSelector(mediaDataSelector.videoList);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const recommendationsList = useSelector(mediaDataSelector.recommendationsList);
  const mediaId = useSelector(videoDataSelector.mediaId);
  const loadingPlayer = useSelector(playerDataSelector.loadingPlayer);
  const isFatalError = useSelector(systemGeneralSelector.isFatalError);

  const showMoreVideos = playerSize !== 'small' && playerSize !== 'extraSmall';
  const clickToPlayMode = videoTagStatus === 'idle' && playbackMethod === 'none';
  const showMoreVideosButton = showControls && !clickToPlayMode && !showGalleryView && !adBreak && !(loadingPlayer || isFatalError);
  const showGalleryPlaylistView = showGalleryView && playerSize !== 'medium';
  const isEndState = videoList.length === 1 && videoTagStatus === 'complete';
  const showMiddleScreenPlaylistView = showGalleryView && playerSize === 'medium';

  const [updatedVideoList, setUpdatedVideoList] = useState(null);
  const [galleryViewOnPause, setGalleryViewOnPause] = useState(false);
  const [exitGalleryView, setExitGalleryView] = useState(false);
  const [inEndState, setInEndState] = useState(false);

  const styles = useStyles(getMoreVideosStyleObject, []);
  const { moreVideosContainerStyle } = styles;

  useEffect(() => {
    if (userInteractionType === 'moreVideosUnitSelected') {
      dispatch(setAdCustomParams('video_experience', 'more video'));
    } else if (videoTagStatus === 'unloaded') {
      dispatch(removeAdCustomParams('video_experience'));
    }
  }, [videoTagStatus, userInteractionType, dispatch]);

  useEffect(() => {
    if (videoList.length === 1) {
      setUpdatedVideoList(recommendationsList.filter(item => item.mediaId !== mediaId));
    } else {
      setUpdatedVideoList(videoList.filter(item => item.mediaId !== mediaId));
    }
  }, [videoList, recommendationsList, mediaId]);

  useEffect(() => {
    if (userInteractionType === 'pause' && videoTagStatus === 'paused' && !exitGalleryView && (playerSize === 'large' || playerSize === 'extraLarge')) {
      setGalleryViewOnPause(true);
      dispatch(setShowGalleryView(true));
    } if ((videoTagStatus === 'playing' || adBreak) && galleryViewOnPause) {
      setGalleryViewOnPause(false);
      dispatch(setShowGalleryView(false));
    } if ((videoTagStatus === 'playing' || adBreak) && inEndState) {
      setInEndState(false);
      setExitGalleryView(false);
      dispatch(setShowGalleryView(false));
    } if (videoDuration !== 0 && !exitGalleryView && videoDuration - currentVideoTimeFragment <= MORE_VIDEOS_SHOW_GALLERY_PLAYLIST_VIEW_SECONDS_TO_END) {
      dispatch(setShowGalleryView(true));
    } if (isEndState) {
      dispatch(setShowGalleryView(true));
      setInEndState(true);
    } if ((videoTagStatus === 'complete' || videoTagStatus === 'unloaded') && !isEndState) {
      setExitGalleryView(false);
      dispatch(setShowGalleryView(false));
    }
  }, [videoDuration, currentVideoTimeFragment, userInteractionType, playerSize, dispatch, videoTagStatus, galleryViewOnPause, exitGalleryView, isEndState, inEndState, adBreak]);

  return (
    <div style={style}>
      <ErrorBoundary pluginName={MORE_VIDEOS_PLUGIN}>
        { showMoreVideos && (
          <div style={moreVideosContainerStyle}>
            { showMoreVideosButton && <MoreVideosButton /> }
            { showGalleryPlaylistView && <GalleryPlaylistView updatedVideoList={updatedVideoList} setExitGalleryView={setExitGalleryView} /> }
            { showMiddleScreenPlaylistView && <MiddleScreenPlaylistView updatedVideoList={updatedVideoList} setExitGalleryView={setExitGalleryView} />}
          </div>
        )}
      </ErrorBoundary>
    </div>
  );
};

export default MoreVideos;
